'use client';
import { Breadcrumbs } from '@snack-uikit/breadcrumbs';
import { Scroll } from '@snack-uikit/scroll';
import { usePathname, useRouter } from 'next/navigation';
import { memo } from 'react';

import { HTMLtoJSX } from '@/components/dynamic';
import { NavListItem } from '@/components/static';
import { LoadingOverlay } from '@/components/static';
import { useAppNavigationCtx, usePageDataCtx } from '@/hooks/ctx';
import { useOIDCAuth } from '@/hooks/oidc';
import { useCurrentPlatform } from '@/hooks/useCurrentPlatform';
import { SectionWithNavigation } from '@/layout/SectionWithNavigation';
import { getActiveAppNavigationItem, toAbsolutePath } from '@/utils/navigation';

import { ArticleFooter } from './ArticleFooter';
import s from './styles.module.scss';
import { ArticlePageProps } from './types';
import { removeLinkUnicode, removeTocDepthBlock } from './utils';
import { createTableOfContentItems } from './utils';
import { makeBreadCrumbsList, reactInjectors } from './utils';
import { withRedoc } from './withRedoc';

const MemorizedHTMLtoJSX = memo(HTMLtoJSX);

function Article({ pageData }: ArticlePageProps) {
  const { push } = useRouter();
  const pathname = usePathname();
  const platform = useCurrentPlatform();
  const { appNavigationConfig } = useAppNavigationCtx();
  const { authRequired } = usePageDataCtx();

  const TOCList = createTableOfContentItems(pageData.toc, NavListItem);

  const { isLoading } = useOIDCAuth(!authRequired);

  return (
    <LoadingOverlay
      loaderClassName={s.loading_overlay_layer}
      blurClassName={s.loading_overlay_layer}
      isLoading={authRequired && isLoading}
    >
      <SectionWithNavigation TOCList={TOCList} TOCClassName={s.articles_toc_wrapper} layoutClassName={s.layout_section}>
        <div className={s.article_wrapper}>
          <div className={s.content_with_bread_crumbs}>
            <Breadcrumbs
              className={s.article_breadcrumbs_wrapper}
              size='xs'
              items={makeBreadCrumbsList({
                items: pageData.parents,
                title: pageData.title,
                push,
                pathname: toAbsolutePath(pathname),
                mainPageLink: getActiveAppNavigationItem(appNavigationConfig, platform, 'name')?.path || '/',
              })}
            />

            <Scroll className={s.article_scroll} size='s'>
              <MemorizedHTMLtoJSX
                html={removeTocDepthBlock(removeLinkUnicode(pageData.body))}
                injectors={reactInjectors}
              />
            </Scroll>
          </div>

          <ArticleFooter next={pageData.next} prev={pageData.prev} />
        </div>
      </SectionWithNavigation>
    </LoadingOverlay>
  );
}

export const ArticlePage = withRedoc(Article);
